import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, FileDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { sharedExportarPdf } from "../../utils/shared-functions";

export default function SliceDetailModal({ slice, onClose }) {
    const { t } = useTranslation();
    const dialogRef = useRef(null);
    const contentRef = useRef(null);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (!slice) return undefined;

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        const { body } = document;
        const previousOverflow = body.style.overflow;
        body.style.overflow = "hidden";
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            body.style.overflow = previousOverflow;
        };
    }, [slice, onClose]);

    if (!slice) return null;
    if (typeof document === "undefined") return null;

    const detalleList = Array.isArray(slice.detallePedido) ? slice.detallePedido : [];

    const handleOverlayMouseDown = (e) => {
        if (dialogRef.current && !dialogRef.current.contains(e.target)) {
            onClose();
        }
    };

    const sliceName = slice.name ?? t("label_sin_datos") ?? "";
    const title = `${sliceName} — ${slice.value ?? 0} ${t("label_pedidos") || "pedidos"}`;

    const handleExport = () => {
        if (isExporting || detalleList.length === 0) return;
        const safeName = String(sliceName).trim().replace(/\s+/g, "-").toLowerCase() || "detalle";
        sharedExportarPdf(contentRef, `${safeName}-detalle`, {
            onStart: () => setIsExporting(true),
            onFinish: () => setIsExporting(false),
            onError: () => setIsExporting(false),
        });
    };

    const renderProductos = (productos) => {
        if (!Array.isArray(productos) || productos.length === 0) return "-";
        return (
            <ul className="list-disc list-inside text-neutral-700 space-y-0.5">
                {productos.map((p, i) => (
                    <li key={i}>
                        {p.nombreProducto ?? p.producto} — {p.cantidad ?? 0} {p.unidadMedida ?? ""}
                    </li>
                ))}
            </ul>
        );
    };

    const modal = (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
            onMouseDown={handleOverlayMouseDown}
            role="presentation"
        >
            <div
                ref={dialogRef}
                className="bg-white rounded-xl shadow-xl border border-neutral-200 w-full max-w-5xl max-h-[90vh] flex flex-col"
                role="dialog"
                aria-modal="true"
            >
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-neutral-200">
                    <h3 className="text-lg font-semibold text-neutral-800 leading-snug min-w-0 line-clamp-2">
                        {title}
                    </h3>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={handleExport}
                            disabled={isExporting || detalleList.length === 0}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FileDown className="h-4 w-4" />
                            {isExporting ? `${t("label_exportar_pdf")}…` : t("label_exportar_pdf")}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
                            aria-label={t("label_cerrar")}
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div
                    ref={contentRef}
                    className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 pt-5 bg-white text-black"
                    style={{ fontFamily: "sans-serif" }}
                >
                    {detalleList.length === 0 ? (
                        <div data-pdf-block>
                            <h2 className="text-lg font-semibold text-neutral-900 leading-normal mb-3">
                                {title}
                            </h2>
                            <p className="text-sm text-neutral-500">{t("label_sin_datos")}</p>
                        </div>
                    ) : (
                        <div data-pdf-block>
                            <h2 className="text-lg font-semibold text-neutral-900 leading-normal mb-3 pb-0.5">
                                {title}
                            </h2>
                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                <table className="w-full max-w-full text-sm border-collapse table-fixed">
                                    <colgroup>
                                        <col style={{ width: "4%" }} />
                                        <col style={{ width: "16%" }} />
                                        <col style={{ width: "12%" }} />
                                        <col style={{ width: "10%" }} />
                                        <col style={{ width: "12%" }} />
                                        <col style={{ width: "46%" }} />
                                    </colgroup>
                                    <thead>
                                        <tr className="bg-gray-50 text-xs font-semibold text-gray-600 border-b border-gray-200">
                                            <th scope="col" className="text-left py-2.5 px-3 align-top leading-normal">#</th>
                                            <th scope="col" className="text-left py-2.5 px-3 align-top leading-normal">{t("label_cliente")}</th>
                                            <th scope="col" className="text-left py-2.5 px-3 align-top leading-normal">{t("label_documento")}</th>
                                            <th scope="col" className="text-left py-2.5 px-3 align-top leading-normal">{t("label_placa")}</th>
                                            <th scope="col" className="text-left py-2.5 px-3 align-top leading-normal">{t("label_estado")}</th>
                                            <th scope="col" className="text-left py-2.5 px-3 align-top leading-normal">{t("label_productos")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detalleList.map((det, idx) => (
                                            <tr
                                                key={det.idDetallePedido ?? idx}
                                                className="border-b border-gray-100 align-top"
                                            >
                                                <td className="py-2 px-3 text-gray-700 tabular-nums">{idx + 1}</td>
                                                <td className="py-2 px-3 text-gray-900 break-words">{det.cliente ?? ""}</td>
                                                <td className="py-2 px-3 text-gray-900 break-words">{det.numeroDocumento ?? ""}</td>
                                                <td className="py-2 px-3 text-gray-900 break-words">{det.labelPlaca ?? det.valuePlaca ?? ""}</td>
                                                <td className="py-2 px-3 text-gray-900 break-words">{det.estado ?? ""}</td>
                                                <td className="py-2 px-3 text-gray-900 break-words">
                                                    {renderProductos(det.productos)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-gray-50 border-t border-gray-200 font-semibold text-gray-900">
                                            <td colSpan={7} className="py-2 px-3 text-left">
                                                {t("label_pedidos") || "Pedidos"}
                                            </td>
                                            <td className="py-2 px-3 text-left tabular-nums">
                                                {detalleList.length}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}
